import { Auth0Client } from '@auth0/auth0-spa-js'
import { defineStore } from 'pinia'
import { clientPortalConfig, isAuth0Configured } from '@/config/clientPortalConfig'
import { getAuthUiLocales, getStoredCulture } from '@/utils/culture'

export var authCallbackPath = '/callback'

function isDevAuthEnabled() {
  return clientPortalConfig.useDevAuth === true || String(clientPortalConfig.useDevAuth) === 'true'
}

export function getAuthRedirectUri() {
  return `${window.location.origin}${authCallbackPath}`
}

export function hasAuthCallbackQuery() {
  var params = new URLSearchParams(window.location.search)
  return params.has('code') && params.has('state')
}

export function isAuthCallbackRoute() {
  return window.location.pathname === authCallbackPath
}

export function isAuthCallbackPending() {
  return isAuthCallbackRoute() && hasAuthCallbackQuery()
}

function buildAuth0ClientOptions() {
  return {
    domain: clientPortalConfig.auth0.domain,
    clientId: clientPortalConfig.auth0.clientId,
    authorizationParams: {
      redirect_uri: getAuthRedirectUri(),
      audience: clientPortalConfig.auth0.audience || undefined,
      scope: 'openid profile email',
    },
    cacheLocation: 'localstorage' as const,
    useRefreshTokens: false,
  }
}

function buildLoginAuthorizationParams(culture?: string) {
  return {
    redirect_uri: getAuthRedirectUri(),
    audience: clientPortalConfig.auth0.audience || undefined,
    scope: 'openid profile email',
    ui_locales: getAuthUiLocales(culture || getStoredCulture()),
  }
}

export var useAuthStore = defineStore('auth', {
  state: () => ({
    isAuthenticated: false,
    isLoading: true,
    initialized: false,
    callbackHandled: false,
    user: null as { name?: string; email?: string } | null,
    auth0Client: null as Auth0Client | null,
  }),
  actions: {
    async ensureAuth0Client(runCheckSession: boolean) {
      if (this.auth0Client) {
        return true
      }
      if (!isAuth0Configured()) {
        return false
      }
      this.auth0Client = new Auth0Client(buildAuth0ClientOptions())
      if (runCheckSession) {
        await this.auth0Client.checkSession()
      }
      return true
    },
    finishInit() {
      this.initialized = true
      this.isLoading = false
    },
    async init() {
      if (this.initialized) {
        return
      }
      try {
        if (isDevAuthEnabled()) {
          this.isAuthenticated = true
          return
        }
        if (isAuthCallbackPending()) {
          return
        }
        if (!await this.ensureAuth0Client(true)) {
          return
        }
        this.isAuthenticated = await this.auth0Client!.isAuthenticated()
        if (this.isAuthenticated) {
          var user = await this.auth0Client!.getUser()
          this.user = { name: user?.name, email: user?.email }
        }
      } finally {
        this.finishInit()
      }
    },
    async handleAuthCallback() {
      if (this.callbackHandled) {
        return '/my-dashboard'
      }
      if (isDevAuthEnabled()) {
        this.callbackHandled = true
        this.isAuthenticated = true
        this.finishInit()
        return '/my-dashboard'
      }
      if (!await this.ensureAuth0Client(false)) {
        this.finishInit()
        return '/contact-us'
      }
      if (!hasAuthCallbackQuery()) {
        this.finishInit()
        return '/my-dashboard'
      }
      var { appState } = await this.auth0Client!.handleRedirectCallback()
      this.callbackHandled = true
      this.isAuthenticated = await this.auth0Client!.isAuthenticated()
      if (this.isAuthenticated) {
        var user = await this.auth0Client!.getUser()
        this.user = { name: user?.name, email: user?.email }
      }
      this.finishInit()
      return (appState?.returnTo as string) || '/my-dashboard'
    },
    async login(returnTo = '/my-dashboard', culture?: string) {
      if (isDevAuthEnabled()) {
        this.isAuthenticated = true
        return
      }
      if (!await this.ensureAuth0Client(false)) {
        return
      }
      await this.auth0Client!.loginWithRedirect({
        appState: { returnTo },
        authorizationParams: buildLoginAuthorizationParams(culture),
      })
    },
    async logout() {
      if (isDevAuthEnabled()) {
        this.isAuthenticated = false
        this.user = null
        return
      }
      if (!this.auth0Client) {
        return
      }
      await this.auth0Client.logout({
        logoutParams: {
          returnTo: window.location.origin,
          ui_locales: getAuthUiLocales(getStoredCulture()),
        },
      })
    },
    async getAccessToken() {
      if (isDevAuthEnabled()) {
        return null
      }
      if (!this.auth0Client || !this.isAuthenticated) {
        return null
      }
      try {
        return await this.auth0Client.getTokenSilently({
          authorizationParams: {
            audience: clientPortalConfig.auth0.audience || undefined,
          },
        })
      } catch {
        return null
      }
    },
  },
})
